/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Volume2, Target, Brain, TrendingUp, Headphones } from 'lucide-react';
import EarTrainingLearn from './EarTraining/EarTrainingLearn';
import EarTrainingPractice from './EarTraining/EarTrainingPractice';
import EarTrainingTest from './EarTraining/EarTrainingTest';
import EarTrainingProgress from './EarTraining/EarTrainingProgress';
import FretboardLearn from './FretboardQuiz/FretboardLearn';
import FretboardPractice from './FretboardQuiz/FretboardPractice';
import FretboardTest from './FretboardQuiz/FretboardTest';
import FretboardProgress from './FretboardQuiz/FretboardProgress';
import MobileQuizBanner from './FretboardQuiz/MobileQuizBanner';
import { QuizBannerState } from './FretboardQuiz/NoteRecognitionQuiz';
import { NoteCollection } from '../api';
import { FretPosition } from './Fretboard';

interface MobilePracticePanelProps {
  currentKey: string;
  currentScale: string;
  noteCollection: NoteCollection | null;
  showNoteNames: boolean;
  onSetShowNoteNames: (show: boolean) => void;
  onSetKey: (key: string) => void;
  onSetScale: (scale: string) => void;

  // Practice mode state (controlled by parent)
  practiceSubMode: 'earTraining' | 'fretboardQuiz';
  setPracticeSubMode: (mode: 'earTraining' | 'fretboardQuiz') => void;

  // Fretboard quiz props
  tuning: string[];
  strings: number;
  frets: number;
  fretRange: { start: number; end: number };
  setFretRange: (range: { start: number; end: number }) => void;
  fretboardInteractive: boolean;
  setFretboardInteractive: (interactive: boolean) => void;
  setRootNotePosition: (position: FretPosition | null) => void;
  setUserFretboardAnswers: (answers: FretPosition[]) => void;
  setHighlightedFretPositions: (positions: FretPosition[]) => void;
  correctUserFretboardAnswers: FretPosition[];
  setCorrectUserFretboardAnswers: (answers: FretPosition[]) => void;
  incorrectUserFretboardAnswers: FretPosition[];
  setIncorrectUserFretboardAnswers: (answers: FretPosition[]) => void;
  onClearFretboard: () => void;
  userFretboardAnswers: FretPosition[];
  isStandardTuning: boolean;
}

/**
 * MobilePracticePanel - Practice mode UI for mobile devices
 *
 * Shows practice tabs (Learn/Practice/Test/Progress) with sub-mode selection
 * (Ear Training vs Fretboard Quiz)
 */
const MobilePracticePanel: React.FC<MobilePracticePanelProps> = ({
  currentKey,
  currentScale,
  noteCollection,
  showNoteNames,
  onSetShowNoteNames,
  onSetKey,
  onSetScale,
  practiceSubMode,
  setPracticeSubMode,
  tuning,
  strings,
  frets,
  fretRange,
  setFretRange,
  fretboardInteractive,
  setFretboardInteractive,
  setRootNotePosition,
  setUserFretboardAnswers,
  setHighlightedFretPositions,
  correctUserFretboardAnswers,
  setCorrectUserFretboardAnswers,
  incorrectUserFretboardAnswers,
  setIncorrectUserFretboardAnswers,
  onClearFretboard,
  userFretboardAnswers,
  isStandardTuning
}) => {
  const [activeTab, setActiveTab] = useState('learn');
  const [remountKey, setRemountKey] = useState(0);
  const [quizBannerState, setQuizBannerState] = useState<QuizBannerState | null>(null);

  // Handle practice sub-mode change - clear fretboard when switching to fretboard quiz
  const handlePracticeSubModeChange = (value: 'earTraining' | 'fretboardQuiz') => {
    setPracticeSubMode(value);
    if (value === 'fretboardQuiz') {
      onClearFretboard();
      // Re-enable interactive mode after clearing (onClearFretboard disables it)
      setFretboardInteractive(true);
      setRemountKey(prev => prev + 1);
    }
  };

  // Handle tab change - clear fretboard when switching tabs in fretboard quiz mode
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (practiceSubMode === 'fretboardQuiz') {
      onClearFretboard();
      // Re-enable interactive mode after clearing (onClearFretboard disables it)
      setFretboardInteractive(true);
      setRemountKey(prev => prev + 1);
    }
  };

  return (
    <div className="w-full bg-card border-t mt-2 p-3">
      {/* Practice Type Selector */}
      <div className="mb-3">
        <Select value={practiceSubMode} onValueChange={handlePracticeSubModeChange}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[100]" side="top">
            <SelectItem value="earTraining">
              <div className="flex items-center gap-2">
                <Headphones className="h-4 w-4" />
                Ear Training
              </div>
            </SelectItem>
            <SelectItem value="fretboardQuiz">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4" />
                Fretboard Quiz
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Quiz Banner - Shows between dropdown and tabs when in fretboard quiz mode */}
      {practiceSubMode === 'fretboardQuiz' && (activeTab === 'practice' || activeTab === 'test') && quizBannerState && (
        <div className="mb-3">
          <MobileQuizBanner
            questionText={quizBannerState.questionText}
            score={quizBannerState.score}
            feedback={quizBannerState.feedback}
            hasAnswers={quizBannerState.hasAnswers}
            onNext={quizBannerState.onNext}
            onShowAnswer={quizBannerState.onShowAnswer}
            onClear={quizBannerState.onClear}
            overlay={false}
          />
        </div>
      )}

      {/* Practice Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="learn" className="text-xs px-1 py-2">
            <Volume2 className="h-3 w-3 mr-1" />
            Learn
          </TabsTrigger>
          <TabsTrigger value="practice" className="text-xs px-1 py-2">
            <Target className="h-3 w-3 mr-1" />
            Practice
          </TabsTrigger>
          <TabsTrigger value="test" className="text-xs px-1 py-2">
            <Brain className="h-3 w-3 mr-1" />
            Test
          </TabsTrigger>
          <TabsTrigger value="progress" className="text-xs px-1 py-2">
            <TrendingUp className="h-3 w-3 mr-1" />
            Progress
          </TabsTrigger>
        </TabsList>

        {/* Learn Tab */}
        <TabsContent value="learn" className="mt-3">
          {practiceSubMode === 'earTraining' ? (
            <EarTrainingLearn
              currentKey={currentKey}
              currentScale={currentScale}
              noteCollection={noteCollection}
            />
          ) : (
            <FretboardLearn
              key={`learn-${remountKey}`}
              currentKey={currentKey}
              currentScale={currentScale}
              showNoteNames={showNoteNames}
              onSetShowNoteNames={onSetShowNoteNames}
              onSetKey={onSetKey}
              onSetScale={onSetScale}
            />
          )}
        </TabsContent>

        {/* Practice Tab */}
        <TabsContent value="practice" className="mt-3">
          {practiceSubMode === 'earTraining' ? (
            <EarTrainingPractice
              currentKey={currentKey}
              currentScale={currentScale}
              noteCollection={noteCollection}
              setFretboardInteractive={setFretboardInteractive}
              setRootNotePosition={setRootNotePosition}
              setUserFretboardAnswers={setUserFretboardAnswers}
              setHighlightedFretPositions={setHighlightedFretPositions}
              tuning={tuning}
              fretRange={fretRange}
              onClearFretboard={onClearFretboard}
            />
          ) : (
            <FretboardPractice
              key={`practice-${remountKey}`}
              currentKey={currentKey}
              currentScale={currentScale}
              noteCollection={noteCollection}
              tuning={tuning}
              fretRange={fretRange}
              setFretRange={setFretRange}
              fretboardInteractive={fretboardInteractive}
              setFretboardInteractive={setFretboardInteractive}
              setRootNotePosition={setRootNotePosition}
              setUserFretboardAnswers={setUserFretboardAnswers}
              setHighlightedFretPositions={setHighlightedFretPositions}
              correctUserFretboardAnswers={correctUserFretboardAnswers}
              setCorrectUserFretboardAnswers={setCorrectUserFretboardAnswers}
              incorrectUserFretboardAnswers={incorrectUserFretboardAnswers}
              setIncorrectUserFretboardAnswers={setIncorrectUserFretboardAnswers}
              onClearFretboard={onClearFretboard}
              userFretboardAnswers={userFretboardAnswers}
              isStandardTuning={isStandardTuning}
              isMobile={true}
              onQuizStateChange={setQuizBannerState}
            />
          )}
        </TabsContent>

        {/* Test Tab */}
        <TabsContent value="test" className="mt-3">
          {practiceSubMode === 'earTraining' ? (
            <EarTrainingTest
              currentKey={currentKey}
              currentScale={currentScale}
              noteCollection={noteCollection}
            />
          ) : (
            <FretboardTest
              key={`test-${remountKey}`}
              currentKey={currentKey}
              currentScale={currentScale}
              noteCollection={noteCollection}
              tuning={tuning}
              fretRange={fretRange}
              fretboardInteractive={fretboardInteractive}
              setFretboardInteractive={setFretboardInteractive}
              setRootNotePosition={setRootNotePosition}
              setUserFretboardAnswers={setUserFretboardAnswers}
              setHighlightedFretPositions={setHighlightedFretPositions}
              correctUserFretboardAnswers={correctUserFretboardAnswers}
              setCorrectUserFretboardAnswers={setCorrectUserFretboardAnswers}
              incorrectUserFretboardAnswers={incorrectUserFretboardAnswers}
              setIncorrectUserFretboardAnswers={setIncorrectUserFretboardAnswers}
              onClearFretboard={onClearFretboard}
              userFretboardAnswers={userFretboardAnswers}
              isStandardTuning={isStandardTuning}
              isMobile={true}
              onQuizStateChange={setQuizBannerState}
            />
          )}
        </TabsContent>

        {/* Progress Tab */}
        <TabsContent value="progress" className="mt-3">
          {practiceSubMode === 'earTraining' ? (
            <EarTrainingProgress />
          ) : (
            <FretboardProgress />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MobilePracticePanel;
